import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Panel, PanelGroup, PanelResizeHandle } from 'react-resizable-panels';
import { Button, StatusMessage, useToast } from '@aimsos/ui';
import { useTranslation } from '@aimsos/i18n';
import InboxCritChips from './InboxCritChips.jsx';
import InboxListItem from './InboxListItem.jsx';
import InboxListSkeleton from './InboxListSkeleton.jsx';
import EventDetailHeader from '../EventDetailHeader.jsx';
import { EventDetailBody } from '../EventDetailBody/index.js';
import { EventTabs } from '../RightPanel/index.js';
import { EventDecisionFooter } from '../EventDecision/index.js';
import { ReassignModal } from '../Team/index.js';
import { useEventDetail } from '../../hooks/useEventDetail.js';
import { useErrorToast } from '../../hooks/useErrorToast.js';
import { useTeamActions } from '../../hooks/useTeamActions.js';
import { useWorkQueueContext } from '../../context/useWorkQueueContext.js';
import EventDraftProvider from '../../context/EventDraftProvider.jsx';
import { WORK_QUEUE_PATHS } from '../../constants/inboxViews.js';
import { HIDE_ATTESTATION_TYPES } from '../../constants/eventTypes.js';
import { SCOPE } from '../../constants/mockEvents.js';
import { TraceSlideOut } from '../Trace/index.js';
import {
  INBOX_COLUMN_GAP_CLASS,
  INBOX_LIST_DEFAULT_SIZE_PERCENT,
  INBOX_LIST_MIN_SIZE_PERCENT,
  INBOX_LIST_MAX_SIZE_PERCENT,
  INBOX_THREAD_WIDTH_PX,
} from '../../constants/inboxSplitLayout.js';
import { AlertCircle, GripVertical, Inbox } from 'lucide-react';

export function getNextEventId(events, currentId) {
  const currentIndex = events.findIndex((event) => event.id === currentId);
  if (currentIndex < 0) return events[0]?.id ?? null;
  return events[currentIndex + 1]?.id ?? events[currentIndex - 1]?.id ?? null;
}

/**
 * Split Inbox canvas: list | detail | thread (3 columnas planas).
 */
function InboxSplitView({
  events,
  inboxListTotal,
  inboxCriticalCount,
  selectedId,
  onSelect,
  onSkip,
  onAsk,
  onEscalate,
  scopeTotal,
  teamScopeRestricted = false,
  loading = false,
  error = null,
}) {
  const { t } = useTranslation('work-queues-app');
  const toast = useToast();
  const navigate = useNavigate();
  const { scope, filters, setFilter, clearFilters, refetch, aggregateWarnings, serverTotals, pendingDecisionIds } =
    useWorkQueueContext();
  const criticalOnly = filters.inboxCriticalOnly;
  const { buildMenuItems, reassign, reassignTarget, openReassign, closeReassign } = useTeamActions();
  const emptyTitle = teamScopeRestricted
    ? t('team.emptyScopedTitle')
    : t(scopeTotal === 0 ? 'empty.allClearTitle' : 'empty.noMatchesTitle');
  const emptySubtitle = teamScopeRestricted
    ? t('team.emptyScopedDescription')
    : t(scopeTotal === 0 ? 'empty.allClearDescription' : 'empty.noMatchesDescription');

  // Meeting-agreed delta: the "All" quick-chip clears every active filter (same
  // as the toolbar's Clear all), not just the Critical bucket toggle.
  const handleCritChip = useCallback(
    (critical) => (critical ? setFilter('inboxCriticalOnly', true) : clearFilters()),
    [setFilter, clearFilters],
  );

  const activeId = useMemo(() => {
    if (!selectedId || !events.some((e) => e.id === selectedId)) return null;
    return selectedId;
  }, [events, selectedId]);

  useEffect(() => {
    // Don't strip a deep-linked id while the list is still loading/failed — the
    // target may appear once the fetch resolves.
    if (loading || error) return;
    if (!events.length) {
      if (selectedId) navigate(WORK_QUEUE_PATHS.root, { replace: true });
      return;
    }
    if (activeId) return;
    onSelect(events[0].id);
  }, [events, activeId, selectedId, onSelect, navigate, loading, error]);

  // Surface a failed fetch as a toast too (in addition to the inline error
  // state), once per distinct error so a re-render doesn't spam it.
  useErrorToast(error, t('errorState.title'));

  // Thread/audit come from the in-memory provider, not the preview fetch — so the
  // preview's loading/error is deliberately NOT forwarded to the tabs (it would
  // otherwise hide comments/audit rows during a detail fetch or failure). A failed
  // detail preview (e.g. change-request/preview's MWK500) is surfaced as a toast;
  // the base event still renders from its list-row detail.
  const { event, thread, attestation, auditTrail, loading: detailLoading, error: detailError } = useEventDetail(activeId);
  const isResolving = Boolean(activeId && pendingDecisionIds?.has(activeId));

  // Toast a failed detail preview once per distinct failure.
  useErrorToast(detailError, t('detail.previewErrorTitle'));

  const handlePrimary = useCallback(() => {
    toast.success({ title: t('decision.recordedTitle'), message: t('decision.recordedMessage') });
  }, [toast, t]);

  const [traceEvent, setTraceEvent] = useState(null);
  const handleTrace = useCallback((target) => setTraceEvent(target), []);
  const closeTrace = useCallback(() => setTraceEvent(null), []);

  const handleSkip = useCallback(
    (id) => {
      const nextId = getNextEventId(events, id);
      onSkip(id);
      if (nextId) onSelect(nextId);
    },
    [onSkip, events, onSelect],
  );

  // Both column bodies are hoisted into memoized elements (CT-5308).
  //
  // A drag re-renders every `Panel` in the group on each pointermove — that is
  // how react-resizable-panels applies the new flex-grow. Inline JSX children
  // would be rebuilt on every one of those frames, so React walked the whole
  // list + detail + thread tree ~60 times a second and the handle visibly
  // trailed the cursor. Hoisted this way the element references stay identical
  // across a drag (none of the deps below change while dragging), so React
  // skips reconciling both subtrees entirely and only the panel's own width
  // is touched.
  //
  // This changes nothing about what renders — the markup and props are the
  // same, only when the elements get rebuilt.
  const listColumn = useMemo(
    () => (
      <aside className="flex h-full flex-col gap-2.5 pr-3">
        <div className="shrink-0">
          <InboxCritChips
            totalCount={inboxListTotal}
            criticalCount={inboxCriticalCount}
            criticalOnly={criticalOnly}
            onChange={handleCritChip}
            loading={loading}
          />
          {aggregateWarnings?.length > 0 && (
            <p className="mt-2 px-1 text-[11px] leading-snug text-amber-600" role="status">
              {t('inbox.partialLoadWarning')}
            </p>
          )}
          {serverTotals?.totalItems != null && serverTotals.totalItems > scopeTotal && !loading && (
            <p className="mt-1 px-1 text-[11px] leading-snug text-content-faint" role="status">
              {t('inbox.truncatedHint', { loaded: scopeTotal, total: serverTotals.totalItems })}
            </p>
          )}
        </div>
        <div className="min-h-0 flex-1 overflow-y-auto pr-2 scrollbar-aimsos-auto-sm">
          {loading ? (
            <InboxListSkeleton />
          ) : error ? (
            <div className="flex flex-col items-center gap-3 px-3 py-10 text-center">
              <StatusMessage
                icon={AlertCircle}
                title={t('errorState.title')}
                subtitle={error}
              />
              <Button variant="secondary" size="small" onClick={refetch}>
                {t('errorState.retry')}
              </Button>
            </div>
          ) : events.length === 0 ? (
            <div className="flex flex-col items-center gap-3 px-3 py-10 text-center">
              <StatusMessage
                icon={Inbox}
                title={emptyTitle}
                subtitle={emptySubtitle}
              />
              {!teamScopeRestricted && scopeTotal > 0 ? (
                <Button variant="secondary" size="small" onClick={clearFilters}>
                  {t('empty.clearFilters')}
                </Button>
              ) : null}
            </div>
          ) : (
            <div className="flex flex-col gap-2">
              {events.map((item) => (
                <InboxListItem
                  key={item.id}
                  event={item}
                  selected={item.id === activeId}
                  onSelect={onSelect}
                />
              ))}
            </div>
          )}
        </div>
      </aside>
    ),
    [
      activeId,
      aggregateWarnings,
      clearFilters,
      criticalOnly,
      emptySubtitle,
      emptyTitle,
      error,
      events,
      handleCritChip,
      inboxCriticalCount,
      inboxListTotal,
      loading,
      onSelect,
      refetch,
      scopeTotal,
      serverTotals,
      t,
      teamScopeRestricted,
    ],
  );

  const detailColumn = useMemo(
    () => (
      <div className="flex h-full min-w-0 flex-1 flex-col gap-4 pl-3">
        {event ? <EventDetailHeader event={event} showBack={false} /> : null}

        <div className={`flex min-h-0 flex-1 items-stretch ${INBOX_COLUMN_GAP_CLASS}`}>
          {/* Col 2 — detalle */}
          <section className="flex min-w-0 flex-1 flex-col overflow-hidden rounded-[10px] border border-edge bg-surface">
            {!event ? (
              <div className="flex flex-1 items-center justify-center p-6">
                <StatusMessage
                  icon={Inbox}
                  title={t('views.selectTitle')}
                  subtitle={t('views.selectSubtitle')}
                />
              </div>
            ) : (
              // Keyed per event so an unsaved inline draft never leaks into the next one.
              <EventDraftProvider key={event.id}>
                <div className="min-h-0 flex-1 overflow-y-auto px-5 py-4 scrollbar-aimsos-auto-sm">
                  <EventDetailBody event={event} flush loading={detailLoading || isResolving} />
                </div>
                <EventDecisionFooter
                  key={event.id}
                  event={event}
                  actions={{
                    primary: handlePrimary,
                    skip: handleSkip,
                    ask: onAsk,
                    escalate: onEscalate,
                    trace: handleTrace,
                    openReassign,
                    // Take it / Nudge / Reassign belong to the TEAM VIEW, not to a
                    // property of the event — the prototype's own tour: "They only
                    // appear in team view, since they only make sense for work you're
                    // not personally holding." Gating on `owner !== undefined` leaked
                    // them into My Work, where every real task is assigned to the
                    // viewer (so "Take it" meant claiming what you already hold).
                    // `owner !== undefined` still applies on top: without an owner
                    // there is nobody to nudge and nothing to take over — and only
                    // `task` rows carry one, because no governance endpoint returns
                    // an assignee (`change-request/list.assignedTo` is hardcoded null
                    // server-side: "no DB source yet").
                    extraMenuItems:
                      scope === SCOPE.MY_TEAM && event.owner !== undefined ? buildMenuItems(event) : [],
                    sticky: false,
                  }}
                />
              </EventDraftProvider>
            )}
          </section>

          {/* Col 3 — thread */}
          <aside
            className="flex shrink-0 flex-col overflow-hidden"
            style={{ width: INBOX_THREAD_WIDTH_PX }}
          >
            {event ? (
              <EventTabs
                key={event.id}
                thread={thread}
                attestation={attestation}
                auditTrail={auditTrail}
                eventId={event.id}
                event={event}
                layout="panel"
                hideAttestation={HIDE_ATTESTATION_TYPES.has(event.type)}
              />
            ) : (
              <div className="flex flex-1 items-center justify-center p-4 text-xs text-content-faint">
                {t('views.selectSubtitle')}
              </div>
            )}
          </aside>
        </div>
      </div>
    ),
    [
      attestation,
      auditTrail,
      buildMenuItems,
      detailLoading,
      event,
      handlePrimary,
      handleSkip,
      handleTrace,
      isResolving,
      onAsk,
      onEscalate,
      openReassign,
      scope,
      t,
      thread,
    ],
  );

  return (
    <>
      <PanelGroup
        direction="horizontal"
        id="work-queue-inbox-split"
        className="flex min-h-0 flex-1 items-stretch overflow-hidden"
      >
        {/*
          Col 1 — lista. Resizable via react-resizable-panels (CT-5308): capped
          at a third of the split so it can never crowd out the detail/thread
          columns — see the team's 2026-08-26 V1 note this replaces in git
          history.
        */}
        <Panel
          id="work-queue-inbox-list-panel"
          order={1}
          defaultSize={INBOX_LIST_DEFAULT_SIZE_PERCENT}
          minSize={INBOX_LIST_MIN_SIZE_PERCENT}
          maxSize={INBOX_LIST_MAX_SIZE_PERCENT}
        >
          {listColumn}
        </Panel>

        <PanelResizeHandle
          className={[
            'group relative w-1 shrink-0 cursor-col-resize bg-gray-200 transition-colors',
            'dark:bg-gray-700 hover:bg-blue-500 dark:hover:bg-blue-600',
            'data-[resize-handle-active]:bg-blue-500 dark:data-[resize-handle-active]:bg-blue-600',
          ].join(' ')}
        >
          <div className="absolute inset-y-0 left-1/2 flex -translate-x-1/2 items-center justify-center">
            <div
              className={[
                'rounded-full bg-gray-300 p-1 opacity-0 transition-opacity',
                'dark:bg-gray-600 group-hover:opacity-100',
                'group-hover:bg-blue-500 dark:group-hover:bg-blue-600',
                'group-data-[resize-handle-active]:opacity-100',
                'group-data-[resize-handle-active]:bg-blue-500 dark:group-data-[resize-handle-active]:bg-blue-600',
              ].join(' ')}
            >
              <GripVertical className="size-3 text-white" aria-hidden />
            </div>
          </div>
        </PanelResizeHandle>

        {/*
          Header sits above both the detail card and the thread (prototype), so
          it spans them instead of living inside the card.
        */}
        <Panel
          id="work-queue-inbox-rest-panel"
          order={2}
          defaultSize={100 - INBOX_LIST_DEFAULT_SIZE_PERCENT}
          minSize={100 - INBOX_LIST_MAX_SIZE_PERCENT}
        >
          {detailColumn}
        </Panel>
      </PanelGroup>

      <ReassignModal
        isOpen={!!reassignTarget}
        event={reassignTarget}
        onClose={closeReassign}
        onReassign={reassign}
      />

      <TraceSlideOut event={traceEvent} isOpen={!!traceEvent} onClose={closeTrace} />
    </>
  );
}

export default InboxSplitView;
